import { FloatingPortal, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { useState, type ReactNode } from 'react';

import { cn } from '@poseidon/components/cn';
import { Tooltip } from '@poseidon/components/Input2';

// Stable middleware identity (re-anchors every pan/zoom frame): `flip` opens upward when there's no room
// below, `shift` nudges it to stay on-screen.
const MIDDLEWARE = [offset(6), flip({ padding: 8 }), shift({ padding: 8 })];

interface Props {
  /** Leading icon, already sized + colored by the caller (e.g. `<Time.Clock className="size-4 ..." />`). */
  icon: ReactNode;
  /** The pill text (e.g. "Wait 1 week", "Await accept · up to 5 days"). */
  label: string;
  /** Shared chip classes (grab cursor, hover shadow, invalid ring) from the node. */
  chipBase: string;
  /** Hover tooltip (e.g. a validation reason). Omit → no tooltip (the label is self-descriptive). */
  tooltip?: ReactNode;
  /** aria-label for the click-to-edit trigger (e.g. "Edit wait duration"). */
  editAriaLabel: string;
  /** The editor rendered in the popover on click. Falsy (locked view) → a static, non-clickable pill. */
  children?: ReactNode;
}

/**
 * A timing step ("wait" / "await accept") as a compact static pill that opens its editable duration in a
 * small popover ONLY when clicked — so the graph reads cleanly and the editor isn't always eating space.
 * The popover is **portaled** (like {@link SequenceGraphInsert}) so it escapes every node's stacking
 * context (never painted under an adjacent node), renders at natural size regardless of zoom, and can't be
 * clipped by the canvas. Locked view (no editor `children`) is just the label. Shared by
 * {@link SequenceWaitNode} and {@link SequenceAwaitNode} so the two timing pills stay identical.
 */
export function SequencePillPopover({ icon, label, chipBase, tooltip, editAriaLabel, children }: Props) {
  const [editing, setEditing] = useState(false);
  // Locked view passes no editor (`children` is `false`/`null`) → a static, non-clickable label.
  const editable = Boolean(children);
  const { refs, floatingStyles } = useFloating({
    open: editing,
    // Prefer ABOVE the node; `flip` drops it below only when there isn't room above.
    placement: 'top',
    strategy: 'fixed',
    // Track the pill as the canvas is panned/zoomed via CSS transform (which scroll/resize observers miss).
    whileElementsMounted: (reference, floating, update) => autoUpdate(reference, floating, update, { animationFrame: true }),
    middleware: MIDDLEWARE,
  });

  const pill = (
    <Tooltip
      as="div"
      tooltip={tooltip}
      className={cn(
        'flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full border border-lynch-200 bg-white px-3 dark:border-slate-700 dark:bg-slate-900',
        editable && 'cursor-pointer',
        chipBase,
      )}>
      {icon}
      <span className="px-0.5 text-xs font-medium text-lynch-600 dark:text-lynch-300">{label}</span>
    </Tooltip>
  );

  if (!editable) return pill;

  return (
    <>
      {/* Only OPENS on click — closing is handled by blur-out / Escape. (A toggle here would reopen: the
          same click's blur-out already closes the popover, then a toggle would flip it back on.) */}
      <button
        ref={refs.setReference}
        type="button"
        aria-label={editAriaLabel}
        onClick={() => !editing && setEditing(true)}
        className="block">
        {pill}
      </button>
      {editing && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            data-no-pan
            className="z-50 rounded-lg border border-lynch-200 bg-white p-1.5 shadow-md dark:border-slate-700 dark:bg-slate-900"
            // Commit-then-close: each field commits on its own blur; close only when focus leaves the WHOLE
            // popover (not when moving between the number field + unit toggle) so an in-flight edit is never dropped.
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) setEditing(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setEditing(false);
            }}>
            {children}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
